/**
 * API client utilities for network operations
 */

import { TIMEOUTS } from '@/config';

export class TimeoutError extends Error {
  constructor(message: string = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Wraps a promise or promise-like object with a timeout
 * @param promise The promise or thenable to wrap
 * @param timeoutMs Timeout in milliseconds (default: 30s)
 * @returns The promise result or throws TimeoutError
 */
export async function withTimeout<T>(
  promise: Promise<T> | PromiseLike<T>,
  timeoutMs: number = TIMEOUTS.API_REQUEST,
): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new TimeoutError(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  // Ensure we have a true Promise
  const actualPromise = Promise.resolve(promise);
  return Promise.race([actualPromise, timeoutPromise]);
}
